// Open water on a body whose registry row carries an `ocean` block: mask, water colour, mirrored sky and glint.
import type * as THREE from 'three';
import type { Ocean } from '../world/types';

/** `SEA_GLSL`'s uniforms, merged into `atmosphereView`'s terrain uniforms; `uSeaOn` 0 is the ground without the sea. */
export function seaUniforms(): Record<string, THREE.IUniform> {
  return {
    uSeaOn: { value: 0 },
    uSeaFresnelOn: { value: 1 },
    /** 0 while the body's air layer is off: no sky drawn, none to mirror. */
    uSeaSkyOn: { value: 0 },
    uSeaMask: { value: [0, 1] },
    uSeaBody: { value: [0, 0, 0] },
    uSeaF0: { value: 0 },
    uSeaRough: { value: 1 },
  };
}

/** Normal-incidence Fresnel reflectance of a liquid of index n in vacuum. */
export function fresnelF0(n: number): number {
  return ((n - 1) / (n + 1)) ** 2;
}

/** three's roughness for a Cox-Munk mean-square slope: GGX at alpha squared = sigma squared is their glint near the peak. */
export function seaRoughness(slopeVariance: number): number {
  return Math.sqrt(Math.sqrt(slopeVariance));
}

/** `sea-check`'s controls: `off` is the old sea, `noFresnel` drops the water surface, `noSky` only the mirrored sky. */
export const seaDebug = { off: false, noFresnel: false, noSky: false };
let oceanMounted = false;

export function syncSeaDebug(u: Record<string, THREE.IUniform>): void {
  u['uSeaOn']!.value = oceanMounted && !seaDebug.off ? 1 : 0;
  u['uSeaFresnelOn']!.value = seaDebug.noFresnel ? 0 : 1;
}

/** Write a row's ocean (or none) into the shared uniforms. */
export function setSeaUniforms(u: Record<string, THREE.IUniform>, ocean: Ocean | null | undefined): void {
  oceanMounted = !!ocean;
  syncSeaDebug(u);
  if (!ocean) return;
  u['uSeaMask']!.value = [ocean.mask_blue_fraction[0], ocean.mask_blue_fraction[1]];
  u['uSeaBody']!.value = [...ocean.body_reflectance_rgb];
  u['uSeaF0']!.value = fresnelF0(ocean.refractive_index);
  u['uSeaRough']!.value = seaRoughness(ocean.slope_variance);
}

/** `seaMaskOf`'s twin on an 8-bit sRGB texel: its blue share, ramped across the row's two bounds. */
export function seaMaskOfSrgb8(r: number, g: number, b: number, lo: number, hi: number): number {
  const bf = b / (r + g + b + 1);
  const t = Math.min(1, Math.max(0, (bf - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
}

/**
 * Declarations and functions. Needs the atmosphere's consumer GLSL ahead of it
 * for the sky march, and `uAtmoFrame`-style frames are the caller's business:
 * every direction handed in shares one frame whose origin is the body centre.
 */
export const SEA_GLSL = /* glsl */ `
uniform float uSeaOn;
uniform float uSeaFresnelOn;
uniform float uSeaSkyOn;
uniform vec2 uSeaMask;
uniform vec3 uSeaBody;
uniform float uSeaF0;
uniform float uSeaRough;
#define SEA_SKY_STEPS 10

float seaMaskOf(vec3 lin) {
  if (uSeaOn < 0.5) return 0.0;
  // Back to the texel's own sRGB byte, so the mask is seaMaskOfSrgb8's on lake-dark texels too.
  vec3 c = max(lin, vec3(0.0));
  vec3 s = mix(pow(c, vec3(1.0 / 2.4)) * 1.055 - 0.055, c * 12.92, step(c, vec3(0.0031308)));
  return smoothstep(uSeaMask.x, uSeaMask.y, s.b / (s.r + s.g + s.b + 1.0 / 255.0));
}

// The albedo's own sea is shaded bathymetry, darker than water is: raised to
// the open ocean's water-leaving reflectance, and shelves already brighter keep theirs.
vec3 seaBodyOf(vec3 lin) { return max(lin, uSeaBody); }

float seaFresnel(float nv) {
  float k = 1.0 - clamp(nv, 0.0, 1.0);
  return uSeaF0 + (1.0 - uSeaF0) * k * k * k * k * k;
}

// Sky radiance per unit sun illuminance along dir from the sea surface under
// up: the sky-view table's march, from the fragment's own ground rather than
// the eye, so it reads the same from orbit as from the shore.
vec3 seaSkyRadiance(vec3 up, vec3 dir, vec3 sunDir) {
  float rg = uAtmoRadii.x;
  vec3 pos = up * (rg + 2.0);
  float tTop = atmoRaySphere(pos, dir, uAtmoRadii.y);
  if (tTop <= 0.0) return vec3(0.0);
  float c = dot(dir, sunDir);
  float phaseR = atmoRayleighPhase(c);
  vec3 phaseM = atmoMiePhase(c);
  vec3 throughput = vec3(1.0);
  vec3 sum = vec3(0.0);
  float tPrev = 0.0;
  for (int i = 1; i <= SEA_SKY_STEPS; i++) {
    float f = float(i) / float(SEA_SKY_STEPS);
    float t = tTop * f * f;
    float dt = t - tPrev;
    vec3 p = pos + dir * (0.5 * (t + tPrev));
    tPrev = t;
    float r = max(length(p), rg);
    vec3 sR; vec3 sM; vec3 ext;
    atmoMedium(r - rg, sR, sM, ext);
    vec3 seg = exp(-ext * dt);
    float muSun = dot(p / r, sunDir);
    float lit = atmoRaySphere(p, sunDir, rg) < 0.0 ? 1.0 : 0.0;
    vec3 s = lit * atmoTransmittanceToTop(r, muSun) * (sR * phaseR + sM * phaseM)
      + atmoMultiScatter(r, muSun) * (sR + sM);
    sum += throughput * (s - s * seg) / max(ext, vec3(1e-14));
    throughput *= seg;
  }
  return min(sum, atmoInScatterMax(throughput, phaseR, phaseM, uAtmoGroundAlbedo));
}

// The sky mirrored in the surface, Fresnel-weighted: N and V (fragment to eye)
// in the same body-centred frame as sunDir.
vec3 seaReflectedSky(vec3 N, vec3 V, vec3 sunDir) {
  if (uSeaFresnelOn < 0.5 || uSeaSkyOn < 0.5) return vec3(0.0);
  vec3 R = reflect(-V, N);
  R = normalize(R + N * max(0.0, 0.02 - dot(R, N)));
  return seaFresnel(dot(N, V)) * seaSkyRadiance(N, R, sunDir) * uAtmoWhite;
}

// three's BRDF_GGX at the water's slope and F0, for the plot's hand-lit shader.
vec3 seaSpecular(vec3 N, vec3 L, vec3 V) {
  float alpha = uSeaRough * uSeaRough;
  float a2 = alpha * alpha;
  vec3 H = normalize(L + V);
  float nl = clamp(dot(N, L), 0.0, 1.0);
  float nv = clamp(dot(N, V), 0.0, 1.0);
  float nh = clamp(dot(N, H), 0.0, 1.0);
  float vh = clamp(dot(V, H), 0.0, 1.0);
  float fr = exp2((-5.55473 * vh - 6.98316) * vh);
  float F = uSeaF0 * (1.0 - fr) + fr;
  float gv = nl * sqrt(a2 + (1.0 - a2) * nv * nv);
  float gl = nv * sqrt(a2 + (1.0 - a2) * nl * nl);
  float vis = 0.5 / max(gv + gl, 1e-6);
  float d = nh * nh * (a2 - 1.0) + 1.0;
  return vec3(F * (vis * a2 / (3.141592653589793 * d * d)));
}
`;

/**
 * The three substitutions a `MeshStandardMaterial` consumer takes: mask and
 * body colour on the finished albedo, the water's F0 on three's own GGX (so
 * the sun glint is three's point light), and the mirrored sky into the
 * indirect specular, in the orrery units `uAtmoGroundScale` then converts.
 * Needs `uAtmoFrame` and `uAtmoTerrainSunDir` from the aerial chunk's decl.
 */
export function applySeaChunk(shader: { fragmentShader: string }): void {
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
  float fxSeaM = seaMaskOf(diffuseColor.rgb);
  diffuseColor.rgb = mix(diffuseColor.rgb, seaBodyOf(diffuseColor.rgb), fxSeaM);
  roughnessFactor = mix(roughnessFactor, uSeaRough, fxSeaM * uSeaFresnelOn);`)
    .replace('#include <lights_physical_fragment>', `#include <lights_physical_fragment>
  material.specularColor = mix(material.specularColor, vec3(uSeaF0), fxSeaM * uSeaFresnelOn);`)
    .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
  if (fxSeaM > 0.0) {
    reflectedLight.indirectSpecular += fxSeaM * seaReflectedSky(normalize(uAtmoFrame * normal),
      normalize(uAtmoFrame * vViewPosition), uAtmoTerrainSunDir)
      * uAtmoSunIrradiance / max(uAtmoGroundScale, 1e-9);
  }`);
}
